import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/authStore';
import {
  Home,
  Layers,
  Sparkles,
  BookOpen,
  FolderKanban,
  FileText,
  MessageSquare,
  User,
  Shield,
  PlusCircle,
  Inbox,
  Activity,
  Menu
} from 'lucide-react';

interface MobileBottomNavProps {
  onOpenDrawer?: () => void;
}

export const MobileBottomNav: React.FC<MobileBottomNavProps> = ({ onOpenDrawer }) => {
  const location = useLocation();
  const { isAuthenticated, isStaff } = useAuth();
  const path = location.pathname;

  // Determine current portal context
  const isClientPortal = path.startsWith('/client');
  const isAdminPortal = path.startsWith('/admin');

  // Active check helper
  const isActive = (targetPath: string) => {
    if (targetPath === '/' && path === '/') return true;
    if (targetPath !== '/' && path.startsWith(targetPath)) return true;
    return false;
  };

  return (
    <nav
      aria-label="Mobile Navigation Bar"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-slate-950/95 backdrop-blur-lg border-t border-slate-200 dark:border-slate-800 shadow-[0_-4px_20px_rgba(0,0,0,0.08)] px-2 py-1.5 pb-safe"
    >
      <div className="flex items-center justify-around max-w-lg mx-auto">
        
        {/* PUBLIC MODE */}
        {!isClientPortal && !isAdminPortal && (
          <>
            <Link
              to="/"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/') && path === '/'
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/') && path === '/' ? 'bg-brand-50' : ''}`}>
                <Home className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Home</span>
            </Link>

            <Link
              to="/services"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/services')
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/services') ? 'bg-brand-50' : ''}`}>
                <Layers className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Services</span>
            </Link>

            {/* Elevated Android Floating Action Pill (Center) */}
            <Link
              to="/submit-enquiry"
              className="flex flex-col items-center justify-center -mt-5 transition-all active:scale-90 group"
              title="Submit Publishing Enquiry"
            >
              <div className="w-12 h-12 rounded-full bg-brand-600 group-hover:bg-brand-700 text-white flex items-center justify-center shadow-lg shadow-brand-600/40 ring-4 ring-white dark:ring-slate-950 transition-transform">
                <PlusCircle className="w-6 h-6" />
              </div>
              <span className="text-[10px] font-bold text-brand-800 dark:text-brand-300 font-sans tracking-tight mt-0.5">
                Submit RFQ
              </span>
            </Link>

            <Link
              to="/publications"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/publications')
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/publications') ? 'bg-brand-50' : ''}`}>
                <BookOpen className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Books</span>
            </Link>

            {isAuthenticated ? (
              <Link
                to={isStaff ? '/admin' : '/client/dashboard'}
                className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                  isActive('/client') || isActive('/admin')
                    ? 'text-brand-700 font-bold'
                    : 'text-slate-500 hover:text-slate-800'
                }`}
              >
                <div className={`p-1 rounded-full ${isActive('/client') || isActive('/admin') ? 'bg-brand-50' : ''}`}>
                  {isStaff ? <Shield className="w-5 h-5" /> : <User className="w-5 h-5" />}
                </div>
                <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">
                  {isStaff ? 'Admin' : 'Portal'}
                </span>
              </Link>
            ) : (
              <button
                type="button"
                onClick={onOpenDrawer}
                className="flex flex-col items-center justify-center py-1 px-3 rounded-2xl text-slate-500 hover:text-slate-800 transition-all active:scale-95"
              >
                <div className="p-1 rounded-full">
                  <Menu className="w-5 h-5" />
                </div>
                <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Menu</span>
              </button>
            )}
          </>
        )}

        {/* CLIENT / AUTHOR PORTAL MODE */}
        {isClientPortal && (
          <>
            <Link
              to="/client/dashboard"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/client/dashboard')
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/client/dashboard') ? 'bg-brand-50' : ''}`}>
                <Home className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Overview</span>
            </Link>

            <Link
              to="/client/projects"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/client/projects')
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/client/projects') ? 'bg-brand-50' : ''}`}>
                <FolderKanban className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Milestones</span>
            </Link>

            <Link
              to="/client/documents"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/client/documents')
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/client/documents') ? 'bg-brand-50' : ''}`}>
                <FileText className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Proofs/Docs</span>
            </Link>

            <Link
              to="/client/messages"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/client/messages')
                  ? 'text-brand-700 font-bold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/client/messages') ? 'bg-brand-50' : ''}`}>
                <MessageSquare className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">Messages</span>
            </Link>

            <button
              type="button"
              onClick={onOpenDrawer}
              className="flex flex-col items-center justify-center py-1 px-3 rounded-2xl text-slate-500 hover:text-slate-800 transition-all active:scale-95"
            >
              <div className="p-1 rounded-full">
                <Menu className="w-5 h-5" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5">More</span>
            </button>
          </>
        )}

        {/* ADMIN BACKEND MODE */}
        {isAdminPortal && (
          <>
            <Link
              to="/admin"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/admin') && path === '/admin'
                  ? 'text-brand-400 font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/admin') && path === '/admin' ? 'bg-slate-800' : ''}`}>
                <Home className="w-5 h-5 text-brand-400" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5 text-slate-300">Overview</span>
            </Link>

            <Link
              to="/admin/enquiries"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/admin/enquiries')
                  ? 'text-brand-400 font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/admin/enquiries') ? 'bg-slate-800' : ''}`}>
                <Inbox className="w-5 h-5 text-brand-400" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5 text-slate-300">Enquiries</span>
            </Link>

            <Link
              to="/admin/projects"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/admin/projects')
                  ? 'text-brand-400 font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/admin/projects') ? 'bg-slate-800' : ''}`}>
                <FolderKanban className="w-5 h-5 text-brand-400" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5 text-slate-300">Projects</span>
            </Link>

            <Link
              to="/admin/documents"
              className={`flex flex-col items-center justify-center py-1 px-3 rounded-2xl transition-all active:scale-95 ${
                isActive('/admin/documents')
                  ? 'text-brand-400 font-bold'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <div className={`p-1 rounded-full ${isActive('/admin/documents') ? 'bg-slate-800' : ''}`}>
                <FileText className="w-5 h-5 text-brand-400" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5 text-slate-300">Docs</span>
            </Link>

            <button
              type="button"
              onClick={onOpenDrawer}
              className="flex flex-col items-center justify-center py-1 px-3 rounded-2xl text-slate-400 hover:text-white transition-all active:scale-95"
            >
              <div className="p-1 rounded-full">
                <Menu className="w-5 h-5 text-slate-300" />
              </div>
              <span className="text-[10px] font-sans font-medium tracking-tight mt-0.5 text-slate-300">More</span>
            </button>
          </>
        )}

      </div>
    </nav>
  );
};
